import { useEffect, useRef, useState } from 'react';
import * as api from '../api';

interface ClassifierState {
  id: number;
  name: string;
  role: string | null;
  probability: number | null;
  accepted: boolean;
  examplePolarity: 'positive' | 'negative' | null;
}

interface Props {
  headlineId: number;
  headlineTitle: string;
  onClose: () => void;
}

export default function ClassifierCorrectionModal({
  headlineId,
  headlineTitle,
  onClose,
}: Props) {
  const [classifiers, setClassifiers] = useState<ClassifierState[]>([]);
  const [hasEmbedding, setHasEmbedding] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [localPolarities, setLocalPolarities] = useState<
    Record<number, 'positive' | 'negative' | null>
  >({});
  const backdropRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api
      .fetchClassifierState(headlineId)
      .then((data) => {
        setClassifiers(data.classifiers);
        setHasEmbedding(data.hasEmbedding);
        const initial: Record<number, 'positive' | 'negative' | null> = {};
        for (const c of data.classifiers) {
          initial[c.id] = c.examplePolarity;
        }
        setLocalPolarities(initial);
        setLoading(false);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      });
  }, [headlineId]);

  const hasChanges = classifiers.some(
    (c) => localPolarities[c.id] !== c.examplePolarity,
  );

  const handleSave = async () => {
    const corrections = classifiers
      .filter((c) => localPolarities[c.id] !== c.examplePolarity)
      .map((c) => ({ classifierId: c.id, polarity: localPolarities[c.id] }));
    if (corrections.length === 0) return;
    setSaving(true);
    setError('');
    try {
      await api.saveClassifierCorrections(headlineId, corrections);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === backdropRef.current) onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  };

  const cyclePolarity = (classifierId: number) => {
    setLocalPolarities((prev) => {
      const current = prev[classifierId];
      // Cycle: null → positive → negative → null
      const next =
        current === null
          ? 'positive'
          : current === 'positive'
            ? 'negative'
            : null;
      return { ...prev, [classifierId]: next };
    });
  };

  const roleLabel = (role: string | null) => {
    if (role === 'interest') return '[+]';
    if (role === 'no-interest') return '[-]';
    return '';
  };

  const polarityLabel = (p: 'positive' | 'negative' | null) => {
    if (p === 'positive') return '+';
    if (p === 'negative') return '-';
    return '\u00B7';
  };

  const polarityClass = (p: 'positive' | 'negative' | null) => {
    if (p === 'positive') return 'correction-positive';
    if (p === 'negative') return 'correction-negative';
    return 'correction-neutral';
  };

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      ref={backdropRef}
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
    >
      <div className="modal-content correction-modal">
        <div className="modal-header">
          <strong>Adjust classifiers</strong>
          <button type="button" className="modal-close" onClick={onClose}>
            &times;
          </button>
        </div>
        <p className="correction-headline-title">{headlineTitle}</p>

        {loading && <p>Loading…</p>}

        {error && <p className="correction-error">{error}</p>}

        {!loading && !hasEmbedding && (
          <p className="correction-no-embedding">
            This headline has no content embedding yet. Corrections will be
            available once the content has been extracted and embedded.
          </p>
        )}

        {!loading && classifiers.length === 0 && hasEmbedding && (
          <p>No classifiers found. Create one first.</p>
        )}

        {!loading && hasEmbedding && classifiers.length > 0 && (
          <ul className="correction-list">
            {classifiers.map((c) => {
              const pol = localPolarities[c.id];
              const changed = pol !== c.examplePolarity;
              return (
                <li
                  key={c.id}
                  className={`correction-row ${changed ? 'correction-changed' : ''} ${hasEmbedding ? 'correction-clickable' : ''}`}
                  onClick={() => hasEmbedding && cyclePolarity(c.id)}
                  onKeyDown={(e) =>
                    e.key === 'Enter' && hasEmbedding && cyclePolarity(c.id)
                  }
                >
                  <span className={`correction-toggle ${polarityClass(pol)}`}>
                    {polarityLabel(pol)}
                  </span>
                  <span className="correction-name">
                    {c.name} {roleLabel(c.role)}
                  </span>
                  {c.probability !== null && (
                    <span
                      className={`correction-prob ${c.accepted ? 'correction-accepted' : ''}`}
                    >
                      {Math.round(c.probability * 100)}%
                      {c.accepted ? ' \u2713' : ''}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )}

        <div className="modal-footer">
          <button type="button" className="modal-cancel" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="save-btn"
            onClick={handleSave}
            disabled={!hasChanges || saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
